import {
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  SimpleGrid,
  Text,
  Tooltip
} from '@mantine/core'
import { FileRejection } from '@mantine/dropzone'
import { useDisclosure, useHotkeys, useViewportSize } from '@mantine/hooks'
import { IconCopy, IconCrop, IconUpload } from '@tabler/icons-react'
import { ReactNode, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { SIZE_UNIT_MAP } from '@/components/subscriptions/usageConfig'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { useUsageLimitModal } from '@/components/subscriptions/useUsageLimitModal'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { MEDIA_UPLOAD_LIMITS } from '@/config/media'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetDatasetVersionFiles,
  useInvalidateFileList
} from '@/queries/datasetQueries'
import { FileStatus } from '@/types/dataset'
import { CrudAction, Resource } from '@/types/permissions'
import { DeleteFilesModal } from '../DeleteFilesModal/DeleteFilesModal'
import { FileSelectionBar } from '../FileSelectionBar/FileSelectionBar'
import { useFileSelection } from '../FileSelectionBar/useFileSelection'
import { MediaCard } from '../MediaCard/MediaCard'
import { MediaViewHandler } from '../MediaViewHandler/MediaViewHandler'
import {
  AcceptedFileTypes,
  UploadDropzone
} from '../UploadDropzone/UploadDropzone'

const UPLOAD_ACCEPTED_FILE_TYPES: AcceptedFileTypes = [
  'jpeg',
  'png',
  'mp4',
  'mov'
]

type UploadableMediaListProps = {
  versionId: string
  isLocked: boolean
  isUploading: boolean
  rightSection?: ReactNode
  showAddCropsButton?: boolean
  showCloneButton?: boolean
  onDrop: (files: File[]) => void
  onReject: (files: FileRejection[]) => void
  onShowUploadProgress: () => void
  onAddCrops: () => void
  onClone: () => void
  onRequestLabeling: () => void
}

export const UploadableMediaList = ({
  versionId,
  isLocked,
  isUploading,
  rightSection,
  showAddCropsButton,
  showCloneButton,
  onDrop,
  onReject,
  onShowUploadProgress,
  onAddCrops,
  onClone,
  onRequestLabeling
}: UploadableMediaListProps) => {
  const openRef = useRef<() => void>(null)

  const [activeFileId, setActiveFileId] = useState<string | null>(null)

  const [
    isDeleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal }
  ] = useDisclosure(false)

  const [canAnnotate] = useHasPermissions(Resource.Annotations, [
    CrudAction.Create
  ])

  const { invalidateFileList } = useInvalidateFileList(versionId)

  const { openUsageLimitModal } = useUsageLimitModal()
  const { usage, refreshFeatureUsage } = useFeatureUsage('dataset_storage')
  const hasSpaceRemaining = !usage || usage.remaining > 0

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetDatasetVersionFiles({
      versionId
    })

  const files = data?.pages?.flatMap((page) => page.results) || []

  const totalFileCount = data?.pages?.[0]?.count

  // Keep only the active file ID (not the whole object) so we always re-fetch the latest file data after the list is refreshed/invalidated.
  const activeFile = files.find((file) => file.id === activeFileId) || null

  const { height } = useViewportSize()

  const {
    selectedFileIds,
    isSelectionModeEnabled,
    toggleFileSelection,
    clearFileSelection,
    selectFiles
  } = useFileSelection()

  useHotkeys([['Escape', clearFileSelection]])

  useHotkeys([
    [
      'mod+A',
      () => {
        if (isSelectionModeEnabled) {
          selectFiles(files.map((file) => file.id))
        }
      }
    ]
  ])

  const handleSelectionChange = (selected: boolean) => {
    if (selected) {
      selectFiles(files.map((file) => file.id))
    } else {
      clearFileSelection()
    }
  }

  const handleUploadClick = () => {
    if (isUploading) {
      onShowUploadProgress()
    } else {
      openRef.current?.()
    }
  }

  const handleFileDelete = () => {
    closeDeleteModal()
    clearFileSelection()
    invalidateFileList()
    void refreshFeatureUsage()
  }

  const handleDrop = (files: File[]) => {
    if (!hasSpaceRemaining) {
      openUsageLimitModal('dataset_storage', {
        maxDatasetStorage: [
          usage?.limit ?? 0,
          SIZE_UNIT_MAP[usage?.unit || 'gb'] || ''
        ]
      })

      return
    }

    onDrop(files)
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="datasets.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Collapse in={isSelectionModeEnabled}>
        <Card bg="gray.1" mb="lg">
          <FileSelectionBar
            allFilesSelected={files.length === selectedFileIds.length}
            fileCount={selectedFileIds.length}
            onSelectionChange={handleSelectionChange}
            onDeleteFiles={openDeleteModal}
          />
        </Card>
      </Collapse>

      {!isSelectionModeEnabled && (
        <Card bg="gray.1" mb="lg">
          <Group justify="space-between" align="end">
            <Group wrap="nowrap" align="end">
              <Tooltip
                disabled={isLocked}
                w={320}
                label={
                  hasSpaceRemaining ? (
                    <>
                      <FormattedMessage id="datasets.upload.uploadFiles.fromComputer" />
                      <br />
                      <FormattedMessage
                        id="datasets.upload.supportedFileTypes"
                        values={{
                          fileTypes: UPLOAD_ACCEPTED_FILE_TYPES.join(', ')
                        }}
                      />
                    </>
                  ) : (
                    <FormattedMessage
                      id="subscriptions.usage.limitReached.datasetStorage"
                      values={{
                        limit: usage?.limit,
                        unit: usage?.unit
                      }}
                    />
                  )
                }
                multiline
              >
                <Button
                  size="sm"
                  miw={140}
                  leftSection={
                    isUploading ? (
                      <Loader size={16} color="white" />
                    ) : (
                      <IconUpload size={16} />
                    )
                  }
                  disabled={isLocked || !hasSpaceRemaining}
                  onClick={handleUploadClick}
                >
                  <FormattedMessage id="datasets.upload.uploadFiles" />
                </Button>
              </Tooltip>

              {showAddCropsButton && (
                <Tooltip
                  label={
                    hasSpaceRemaining ? (
                      <FormattedMessage id="datasets.crops.add.tooltip" />
                    ) : (
                      <FormattedMessage
                        id="subscriptions.usage.limitReached.datasetStorage"
                        values={{
                          limit: usage?.limit,
                          unit: usage?.unit
                        }}
                      />
                    )
                  }
                  w={320}
                  multiline
                >
                  <Button
                    variant="outline"
                    miw={140}
                    leftSection={<IconCrop size={16} />}
                    disabled={!hasSpaceRemaining}
                    onClick={onAddCrops}
                  >
                    <FormattedMessage id="datasets.crops.add" />
                  </Button>
                </Tooltip>
              )}

              {showCloneButton && (
                <Tooltip
                  w={320}
                  label={
                    <FormattedMessage id="datasets.clone.button.tooltip" />
                  }
                  multiline
                >
                  <Button
                    variant="outline"
                    miw={140}
                    leftSection={<IconCopy size={16} />}
                    onClick={onClone}
                  >
                    <FormattedMessage id="datasets.clone.button.label" />
                  </Button>
                </Tooltip>
              )}
            </Group>

            {rightSection}
          </Group>
        </Card>
      )}

      {totalFileCount !== undefined && (
        <Group justify="space-between" align="end" mb="xs">
          <Text size="xs" fw={600}>
            (
            <FormattedMessage
              id="datasets.fileCount"
              values={{ count: totalFileCount }}
            />
            )
          </Text>

          {totalFileCount > 0 && !isLocked && (
            <Button size="xs" variant="outline" onClick={onRequestLabeling}>
              <FormattedMessage id="annotation.requestLabeling.button" />
            </Button>
          )}
        </Group>
      )}

      <UploadDropzone
        openRef={openRef}
        activateOnClick={files.length === 0 && !isFetching}
        showInstructions={files.length === 0 && !isFetching}
        acceptedFileTypes={UPLOAD_ACCEPTED_FILE_TYPES}
        fileLimit={MEDIA_UPLOAD_LIMITS.MAX_FILES_PER_BATCH}
        minHeight={height - 400}
        disabled={isLocked}
        withDisabledStyle={false}
        onDrop={handleDrop}
        onReject={onReject}
      >
        <InfiniteScroll
          hasMore={hasNextPage}
          isFetching={isFetching}
          onLoadMore={() => void fetchNextPage()}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4, xl: 7 }} spacing="xl">
            {files.map((file) => (
              <MediaCard
                key={file.id}
                fileName={file.name}
                thumbnailUrl={file.thumbnail}
                isSelected={selectedFileIds.includes(file.id)}
                isSelectionMode={isSelectionModeEnabled}
                isLabeled={file.status === FileStatus.Labeled}
                isLocked={isLocked}
                isSelectable
                onClick={() => {
                  setActiveFileId(file.id)
                }}
                onSelectionChange={() => toggleFileSelection(file.id)}
              />
            ))}
          </SimpleGrid>
        </InfiniteScroll>
      </UploadDropzone>

      <MediaViewHandler
        opened={!!activeFile}
        activeFile={activeFile}
        files={files}
        datasetVersionId={versionId}
        isAnnotationDisabled={isLocked || !canAnnotate}
        isMediaLoading={isFetching}
        onClose={() => setActiveFileId(null)}
        onActiveFileChange={(file) => setActiveFileId(file.id)}
      />

      <DeleteFilesModal
        opened={isDeleteModalOpened}
        versionId={versionId}
        selectedFileIds={selectedFileIds}
        onDelete={handleFileDelete}
        onClose={closeDeleteModal}
      />
    </>
  )
}
